import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AirportInput from "@/components/AirportInput";
import { Fuel, Loader2, Trash2, ArrowLeft, Plus, Info } from "lucide-react";

/**
 * /operators/fuel-prices
 *
 * Operator dashboard for contract jet-A prices. Anything set here overrides
 * the PJP-network + public fallback layers for THIS operator's quotes,
 * feeding straight into the quote engine's tankering math and the "Ops
 * Factors · Fuel" line on the quote card.
 *
 * Backend contract:
 *   GET  /api/operator/fuel-prices             → { rows: [...] }
 *   POST /api/operator/fuel-prices             → upsert one
 *   DELETE /api/operator/fuel-prices/{icao}    → remove one
 */
export default function OperatorFuelPrices() {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ airport_icao: "", price_per_gal_usd: "", fbo: "", note: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (user?.role !== "charter_operator") { setLoading(false); return; }
    (async () => {
      try {
        const r = await api.get("/operator/fuel-prices");
        setRows(r.data.rows || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load fuel prices");
      } finally {
        setLoading(false);
      }
    })();
  }, [authLoading, user]);

  const isOperator = user?.role === "charter_operator";

  const upsert = async (e) => {
    e?.preventDefault?.();
    if (!draft.airport_icao || !draft.price_per_gal_usd) {
      toast.error("Airport ICAO and price/gal required");
      return;
    }
    const price = parseFloat(draft.price_per_gal_usd);
    if (!Number.isFinite(price) || price <= 0 || price > 30) {
      toast.error("Price/gal must be between $0.01 and $30");
      return;
    }
    setSaving(true);
    try {
      await api.post("/operator/fuel-prices", {
        airport_icao: draft.airport_icao.trim().toUpperCase(),
        price_per_gal_usd: price,
        fbo: draft.fbo.trim() || null,
        note: draft.note.trim() || null,
      });
      const r = await api.get("/operator/fuel-prices");
      setRows(r.data.rows || []);
      setDraft({ airport_icao: "", price_per_gal_usd: "", fbo: "", note: "" });
      toast.success("Fuel price saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (icao) => {
    if (!window.confirm(`Remove your ${icao} contract fuel price?`)) return;
    try {
      await api.delete(`/operator/fuel-prices/${icao}`);
      setRows((rs) => rs.filter((r) => r.airport_icao !== icao));
      toast.success(`${icao} removed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (authLoading || loading) {
    return <div className="max-w-4xl mx-auto px-6 py-14 text-center text-white/60"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  }
  if (!user) return <Navigate to="/login?next=/operators/fuel-prices" replace />;
  if (!isOperator) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="rounded-lg border border-amber-400/40 bg-amber-400/[0.06] p-5">
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-amber-300 mb-1">// Operators only</div>
          <div className="text-white/85">This page is for verified charter operators. <Link className="text-cyan underline" to="/operators/onboard">Onboard your company</Link> to unlock fuel price entry.</div>
        </div>
      </div>
    );
  }

  const sortedRows = [...rows].sort((a, b) => a.airport_icao.localeCompare(b.airport_icao));

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="operator-fuel-prices">
      <Link to="/dashboard" data-testid="op-fuel-back" className="mono text-[10px] tracking-widest uppercase text-cyan/70 hover:text-cyan inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" /> Back to cockpit
      </Link>
      <div className="mt-3 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// Charter Operator · Contract fuel prices</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mt-1 leading-none">
            Your <span className="text-cyan">jet-A</span> deals.
          </h1>
          <div className="text-white/60 mt-2 text-sm max-w-xl">
            Every price you post here overrides the public + PJP-network baseline for your quotes on the PJP engine. Better prices here = tighter tanker math = you win more trips.
          </div>
        </div>
      </div>

      {/* Add / edit row */}
      <form onSubmit={upsert} className="mt-8 rounded-xl border border-cyan/25 bg-cyan/[0.04] p-5" data-testid="op-fuel-add">
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80 mb-3 flex items-center gap-1.5">
          <Plus className="h-3 w-3" /> // Add / update a contract price
        </div>
        <div className="grid md:grid-cols-[1fr_140px_1fr_1fr_120px] gap-3">
          <div>
            <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Airport</div>
            <AirportInput
              testId="op-fuel-icao"
              value={draft.airport_icao}
              onChange={(v) => setDraft((d) => ({ ...d, airport_icao: v }))}
              placeholder="ICAO"
            />
          </div>
          <div>
            <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Price / gal</div>
            <input
              data-testid="op-fuel-price"
              type="number"
              step="0.01"
              min="0"
              max="30"
              value={draft.price_per_gal_usd}
              onChange={(e) => setDraft((d) => ({ ...d, price_per_gal_usd: e.target.value }))}
              placeholder="$5.85"
              className="input w-full"
            />
          </div>
          <div>
            <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">FBO (optional)</div>
            <input
              data-testid="op-fuel-fbo"
              value={draft.fbo}
              onChange={(e) => setDraft((d) => ({ ...d, fbo: e.target.value }))}
              placeholder="Signature · Atlantic · Meridian…"
              className="input w-full"
            />
          </div>
          <div>
            <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Note (optional)</div>
            <input
              data-testid="op-fuel-note"
              value={draft.note}
              onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
              placeholder="Contract · valid Q1"
              className="input w-full"
            />
          </div>
          <div>
            <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1 md:invisible">Save</div>
            <button
              type="submit"
              disabled={saving}
              data-testid="op-fuel-save"
              className="btn-hud btn-hud-solid w-full justify-center disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "SAVE"}
            </button>
          </div>
        </div>
        <div className="mt-3 mono text-[10px] tracking-widest uppercase text-white/40 flex items-center gap-1.5">
          <Info className="h-3 w-3" /> Overrides PJP + public prices for your own quotes only. Other operators still see the network baseline.
        </div>
      </form>

      {/* List */}
      <div className="mt-8" data-testid="op-fuel-list">
        {sortedRows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-white/50" data-testid="op-fuel-empty">
            <Fuel className="h-7 w-7 text-cyan mx-auto mb-2" />
            No contract prices yet. Add the airports where you have negotiated jet-A rates — even 5-10 well-priced fields dramatically tighten your quotes.
          </div>
        ) : (
          <div className="rounded-xl border border-white/10 overflow-hidden">
            <div className="grid grid-cols-[100px_120px_1fr_1fr_140px_40px] gap-3 px-4 py-2 mono text-[10px] tracking-widest uppercase text-white/50 bg-white/[0.03]">
              <div>ICAO</div>
              <div>Price / gal</div>
              <div>FBO</div>
              <div>Note</div>
              <div>Updated</div>
              <div />
            </div>
            {sortedRows.map((r) => (
              <div
                key={r.airport_icao}
                className="grid grid-cols-[100px_120px_1fr_1fr_140px_40px] gap-3 px-4 py-3 border-t border-white/[0.06] items-center"
                data-testid={`op-fuel-row-${r.airport_icao}`}
              >
                <div className="mono text-cyan">{r.airport_icao}</div>
                <div className="mono text-avail">${r.price_per_gal_usd?.toFixed(2)}</div>
                <div className="text-white/85 text-sm truncate">{r.fbo || "—"}</div>
                <div className="text-white/60 text-sm truncate">{r.note || "—"}</div>
                <div className="mono text-[10px] tracking-widest uppercase text-white/40">
                  {r.updated_at ? new Date(r.updated_at).toISOString().slice(0, 10) : "—"}
                </div>
                <button
                  onClick={() => remove(r.airport_icao)}
                  data-testid={`op-fuel-remove-${r.airport_icao}`}
                  className="p-1.5 rounded hover:bg-red-500/10 text-white/40 hover:text-red-300"
                  title="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
